// // plugins/pinia-persist.client.ts
// import type { Pinia } from 'pinia'
// import { createPersistedState } from 'pinia-plugin-persistedstate'
// import { capacitorStorageSync, syncCapacitorToStorage } from '~/utils/persist-storage'
// import { defineNuxtPlugin } from '#app'
//
// /**
//  * Serializer avec chiffrement natif utilisant Web Crypto API
//  */
// class SecureSerializer {
//     private password: string
//     private encoder = new TextEncoder()
//     private decoder = new TextDecoder()
//     private cache = new Map<string, string>()
//     private encryptionQueue = new Map<string, Promise<string>>()
//     private decryptionQueue = new Map<string, Promise<string>>()
//
//     constructor(password: string) {
//         this.password = password
//     }
//
//     /**
//      * Dérive une clé cryptographique à partir du mot de passe
//      */
//     private async deriveKey(salt: Uint8Array): Promise<CryptoKey> {
//         const keyMaterial = await crypto.subtle.importKey(
//             'raw',
//             this.encoder.encode(this.password),
//             'PBKDF2',
//             false,
//             ['deriveBits', 'deriveKey']
//         )
//
//         return crypto.subtle.deriveKey(
//             {
//                 name: 'PBKDF2',
//                 salt,
//                 iterations: 10000,
//                 hash: 'SHA-256'
//             },
//             keyMaterial,
//             { name: 'AES-GCM', length: 256 },
//             false,
//             ['encrypt', 'decrypt']
//         )
//     }
//
//     /**
//      * Chiffre les données de manière asynchrone
//      */
//     async encryptAsync(text: string): Promise<string> {
//         // Éviter les appels récursifs avec une queue
//         if (this.encryptionQueue.has(text)) {
//             return this.encryptionQueue.get(text)!
//         }
//
//         const encryptionPromise = this._performEncryption(text)
//         this.encryptionQueue.set(text, encryptionPromise)
//
//         try {
//             const result = await encryptionPromise
//             return result
//         } finally {
//             this.encryptionQueue.delete(text)
//         }
//     }
//
//     private async _performEncryption(text: string): Promise<string> {
//         try {
//             const salt = crypto.getRandomValues(new Uint8Array(16))
//             const iv = crypto.getRandomValues(new Uint8Array(12))
//             const key = await this.deriveKey(salt)
//
//             const encrypted = await crypto.subtle.encrypt(
//                 { name: 'AES-GCM', iv },
//                 key,
//                 this.encoder.encode(text)
//             )
//
//             // Combiner salt + iv + encrypted
//             const combined = new Uint8Array(salt.length + iv.length + encrypted.byteLength)
//             combined.set(salt, 0)
//             combined.set(iv, salt.length)
//             combined.set(new Uint8Array(encrypted), salt.length + iv.length)
//
//             return btoa(String.fromCharCode(...combined))
//         } catch (error) {
//             console.error('Encryption error:', error)
//             throw new Error(`Encryption failed: ${error}`)
//         }
//     }
//
//     /**
//      * Déchiffre les données de manière asynchrone
//      */
//     async decryptAsync(encryptedText: string): Promise<string> {
//         // Éviter les appels récursifs avec une queue
//         if (this.decryptionQueue.has(encryptedText)) {
//             return this.decryptionQueue.get(encryptedText)!
//         }
//
//         const decryptionPromise = this._performDecryption(encryptedText)
//         this.decryptionQueue.set(encryptedText, decryptionPromise)
//
//         try {
//             const result = await decryptionPromise
//             return result
//         } finally {
//             this.decryptionQueue.delete(encryptedText)
//         }
//     }
//
//     private async _performDecryption(encryptedText: string): Promise<string> {
//         try {
//             const combined = Uint8Array.from(atob(encryptedText), c => c.charCodeAt(0))
//
//             if (combined.length < 28) {
//                 throw new Error('Invalid encrypted data format')
//             }
//
//             const salt = combined.slice(0, 16)
//             const iv = combined.slice(16, 28)
//             const encrypted = combined.slice(28)
//
//             const key = await this.deriveKey(salt)
//
//             const decrypted = await crypto.subtle.decrypt(
//                 { name: 'AES-GCM', iv },
//                 key,
//                 encrypted
//             )
//
//             return this.decoder.decode(decrypted)
//         } catch (error) {
//             console.error('Decryption error:', error)
//             throw new Error(`Decryption failed: ${error}`)
//         }
//     }
//
//     /**
//      * Sérialise de manière synchrone avec chiffrement en arrière-plan
//      */
//     serialize(state: any): string {
//         if (!state || typeof state !== 'object') {
//             return JSON.stringify(state)
//         }
//
//         const json = JSON.stringify(state)
//
//         // Générer une clé de cache basée sur un hash du contenu
//         const cacheKey = this.generateCacheKey(json)
//
//         // Si on a une version chiffrée en cache, la retourner
//         if (this.cache.has(cacheKey)) {
//             return this.cache.get(cacheKey)!
//         }
//
//         // Chiffrer en arrière-plan pour la prochaine fois (sans attendre)
//         this.encryptAsync(json)
//             .then(encrypted => {
//                 const result = JSON.stringify({
//                     _encrypted: true,
//                     data: encrypted,
//                     timestamp: Date.now(),
//                     _fallback: state // Garder une copie fallback
//                 })
//
//                 this.cache.set(cacheKey, result)
//
//                 // Limiter la taille du cache
//                 this.cleanCache()
//             })
//             .catch(error => {
//                 console.error('Background encryption failed:', error)
//             })
//
//         // Retourner le JSON non chiffré pour cette fois
//         return JSON.stringify({
//             _encrypted: false,
//             data: json,
//             timestamp: Date.now(),
//             _fallback: state
//         })
//     }
//
//     /**
//      * Désérialise de manière synchrone
//      */
//     deserialize(data: string): any {
//         if (!data) {
//             return {}
//         }
//
//         try {
//             const parsed = JSON.parse(data)
//
//             // Si c'est un objet avec notre structure
//             if (parsed && typeof parsed === 'object' && '_encrypted' in parsed) {
//                 // Si les données sont chiffrées
//                 if (parsed._encrypted && parsed.data) {
//                     // Déchiffrer en arrière-plan (sans bloquer)
//                     this.decryptAsync(parsed.data)
//                         .then(decrypted => {
//                             const state = JSON.parse(decrypted)
//                             console.log('✅ State decrypted successfully')
//                             // Vous pourriez émettre un événement ici pour notifier les composants
//                         })
//                         .catch(error => {
//                             console.error('❌ Background decryption failed:', error)
//                         })
//
//                     // Retourner le fallback immédiatement
//                     return parsed._fallback || {}
//                 } else {
//                     // Données non chiffrées
//                     return JSON.parse(parsed.data)
//                 }
//             }
//
//             // Format legacy ou données directes
//             return parsed
//         } catch (error) {
//             console.error('Deserialization error:', error)
//             return {}
//         }
//     }
//
//     /**
//      * Génère une clé de cache basée sur le contenu
//      */
//     private generateCacheKey(content: string): string {
//         let hash = 0
//         for (let i = 0; i < Math.min(content.length, 100); i++) {
//             const char = content.charCodeAt(i)
//             hash = ((hash << 5) - hash) + char
//             hash = hash & hash
//         }
//         return Math.abs(hash).toString(16)
//     }
//
//     /**
//      * Nettoie le cache
//      */
//     private cleanCache() {
//         if (this.cache.size > 20) {
//             const entries = Array.from(this.cache.entries())
//             // Garder seulement les 10 plus récents
//             entries.splice(0, entries.length - 10)
//             this.cache.clear()
//             entries.forEach(([key, value]) => {
//                 this.cache.set(key, value)
//             })
//         }
//     }
//
//     /**
//      * Nettoie les ressources
//      */
//     cleanup() {
//         this.cache.clear()
//         this.encryptionQueue.clear()
//         this.decryptionQueue.clear()
//     }
// }
//
// /**
//  * Plugin principal
//  */
// export default defineNuxtPlugin({
//     name: 'pinia-persist',
//     parallel: true,
//
//     async setup(nuxtApp) {
//         const pinia = nuxtApp.$pinia as Pinia
//         const config = useRuntimeConfig()
//
//         // Synchroniser Capacitor vers localStorage au démarrage
//         console.log('🔄 Synchronizing storage...')
//         try {
//             await syncCapacitorToStorage()
//             console.log('✅ Storage synchronized')
//         } catch (error) {
//             console.error('❌ Storage sync error:', error)
//         }
//
//         // Configuration
//         const isProduction = process.env.NODE_ENV === 'production'
//         const isSecureContext = typeof window !== 'undefined' && (
//             window.isSecureContext ||
//             window.location.protocol === 'https:' ||
//             window.location.hostname === 'localhost' ||
//             window.location.hostname === '127.0.0.1'
//         )
//         const shouldEncrypt = isProduction || config.public?.forceEncryption
//         const canEncrypt = shouldEncrypt && isSecureContext && (crypto?.subtle !== undefined)
//
//         // Créer le serializer approprié
//         let serializer: any
//         let secureSerializer: SecureSerializer | null = null
//
//         if (canEncrypt) {
//             console.log('🔒 Encryption enabled (Web Crypto API)')
//
//             const encryptionKey = config.public?.encryptionKey ||
//                 generateDefaultKey(config.public?.appId)
//
//             secureSerializer = new SecureSerializer(encryptionKey)
//             serializer = {
//                 serialize: (state: any) => {
//                     try {
//                         return secureSerializer!.serialize(state)
//                     } catch (error) {
//                         console.error('Serialization error:', error)
//                         return JSON.stringify(state)
//                     }
//                 },
//                 deserialize: (data: string) => {
//                     try {
//                         return secureSerializer!.deserialize(data)
//                     } catch (error) {
//                         console.error('Deserialization error:', error)
//                         try {
//                             return JSON.parse(data)
//                         } catch {
//                             return {}
//                         }
//                     }
//                 }
//             }
//         } else {
//             if (shouldEncrypt && !isSecureContext) {
//                 console.warn('⚠️ Encryption requested but not in secure context (HTTPS required)')
//             }
//
//             console.log('🔓 Using plain JSON serialization')
//             serializer = {
//                 serialize: (state: any) => {
//                     try {
//                         return JSON.stringify(state)
//                     } catch (error) {
//                         console.error('JSON serialization error:', error)
//                         return '{}'
//                     }
//                 },
//                 deserialize: (data: string) => {
//                     try {
//                         return JSON.parse(data)
//                     } catch (error) {
//                         console.error('JSON deserialization error:', error)
//                         return {}
//                     }
//                 }
//             }
//         }
//
//         // Configurer le plugin de persistance
//         pinia.use(createPersistedState({
//             // Utiliser le storage Capacitor-aware
//             storage: capacitorStorageSync,
//
//             // Serializer (avec ou sans chiffrement)
//             serializer,
//
//             // Clé préfixée pour éviter les collisions
//             key: (id: string) => `${process.env.NODE_ENV === 'development' ? 'd':'p'}eco-${id}`,
//
//             // Debug en développement
//             debug: !isProduction && process.env.NODE_ENV === 'development'
//         }))
//
//         // Nettoyer les anciennes données en production (après 10 secondes)
//         if (isProduction) {
//             setTimeout(() => {
//                 cleanupOldData()
//             }, 10000)
//         }
//
//         // Nettoyer les ressources à la fermeture
//         if (typeof window !== 'undefined') {
//             window.addEventListener('beforeunload', () => {
//                 secureSerializer?.cleanup()
//             })
//         }
//
//         // Exposer des utilitaires
//         nuxtApp.provide('persistedStorage', {
//             // Effacer toutes les données de l'app
//             async clearAll() {
//                 try {
//                     // Effacer localStorage
//                     for (let i = localStorage.length - 1; i >= 0; i--) {
//                         const key = localStorage.key(i)
//                         if (key?.startsWith('eco-')) {
//                             localStorage.removeItem(key)
//                         }
//                     }
//
//                     // Effacer Capacitor si on est sur mobile
//                     if ((await import('@capacitor/core')).Capacitor.isNativePlatform()) {
//                         const { Preferences } = await import('@capacitor/preferences')
//                         const { keys } = await Preferences.keys()
//
//                         for (const key of keys) {
//                             if (key.startsWith('eco-')) {
//                                 await Preferences.remove({ key })
//                             }
//                         }
//                     }
//
//                     // Nettoyer le serializer
//                     secureSerializer?.cleanup()
//
//                     console.log('✅ All persisted data cleared')
//                 } catch (error) {
//                     console.error('❌ Error clearing data:', error)
//                 }
//             },
//
//             // Obtenir la taille du stockage
//             getStorageSize(): number {
//                 let size = 0
//                 try {
//                     for (let key in localStorage) {
//                         if (key.startsWith('eco-')) {
//                             const value = localStorage.getItem(key)
//                             if (value) {
//                                 size += key.length + value.length
//                             }
//                         }
//                     }
//                 } catch (error) {
//                     console.error('Error calculating storage size:', error)
//                 }
//                 return size
//             },
//
//             // Vérifier si le chiffrement est activé
//             isEncrypted(): boolean {
//                 return canEncrypt
//             },
//
//             // Forcer la synchronisation avec Capacitor
//             async syncWithCapacitor() {
//                 try {
//                     await syncCapacitorToStorage()
//                 } catch (error) {
//                     console.error('Sync error:', error)
//                     throw error
//                 }
//             }
//         })
//     }
// })
//
// /**
//  * Génère une clé déterministe basée sur l'app name
//  */
// function generateDefaultKey(appName: string = 'eco-construction'): string {
//     // Créer une clé déterministe mais unique par app
//     const baseKey = `${appName}-2024-secure-key`
//     let hash = 0
//
//     for (let i = 0; i < baseKey.length; i++) {
//         const char = baseKey.charCodeAt(i)
//         hash = ((hash << 5) - hash) + char
//         hash = hash & hash
//     }
//
//     return Math.abs(hash).toString(16).padStart(32, '0')
// }
//
// /**
//  * Nettoie les anciennes données
//  */
// function cleanupOldData() {
//     try {
//         const now = Date.now()
//         const maxAge = 30 * 24 * 60 * 60 * 1000 // 30 jours
//
//         for (let i = localStorage.length - 1; i >= 0; i--) {
//             const key = localStorage.key(i)
//             if (!key?.startsWith('eco-')) continue
//
//             try {
//                 const item = localStorage.getItem(key)
//                 if (!item) continue
//
//                 const data = JSON.parse(item)
//                 // Si les données ont un timestamp et sont anciennes
//                 if (data.timestamp && (now - data.timestamp) > maxAge) {
//                     localStorage.removeItem(key)
//                     console.log(`🗑️ Cleaned old data: ${key}`)
//                 }
//             } catch {
//                 // Ignorer les erreurs de parsing
//             }
//         }
//     } catch (error) {
//         console.error('Cleanup error:', error)
//     }
// }
// plugins/pinia-persist.client.ts
import type { Pinia } from 'pinia'
import { createPersistedState } from 'pinia-plugin-persistedstate'
import { capacitorStorageSync, syncCapacitorToStorage } from '~/utils/persist-storage'
import { defineNuxtPlugin } from '#app'
import CryptoJS from 'crypto-js'

// Extension du type Persistence pour ajouter l'option secure
declare module 'pinia-plugin-persistedstate' {
    interface Persistence<S = any, Ser = any, Des = any> {
        secure?: boolean
    }
}

/**
 * Serializer simple JSON (pour les stores non sécurisés)
 */
class SimpleJSONSerializer {
    private storeId: string

    constructor(storeId: string) {
        this.storeId = storeId
    }

    serialize(state: any): string {
        try {
            return JSON.stringify(state)
        } catch (error) {
            console.error(`JSON serialization error for store ${this.storeId}:`, error)
            return '{}'
        }
    }

    deserialize(data: string): any {
        if (!data) {
            return {}
        }

        try {
            return JSON.parse(data)
        } catch (error) {
            console.error(`JSON deserialization error for store ${this.storeId}:`, error)
            return {}
        }
    }

    cleanup() {
        // Rien à nettoyer pour le JSON simple
    }
}

/**
 * Serializer avec chiffrement synchrone utilisant crypto-js
 */
class SecureCryptoJSSerializer {
    private encryptionKey: string
    private storeId: string
    private cache = new Map<string, string>()
    private maxCacheSize = 10

    constructor(encryptionKey: string, storeId: string) {
        this.encryptionKey = this.deriveStoreKey(encryptionKey, storeId)
        this.storeId = storeId
    }

    /**
     * Dérive une clé unique pour chaque store
     */
    private deriveStoreKey(baseKey: string, storeId: string): string {
        return CryptoJS.PBKDF2(baseKey + storeId, 'eco-construction-salt', {
            keySize: 256/32,
            iterations: 1000
        }).toString()
    }

    /**
     * Chiffre les données de manière synchrone
     */
    private encrypt(text: string): string {
        try {
            const encrypted = CryptoJS.AES.encrypt(text, this.encryptionKey).toString()
            return encrypted
        } catch (error) {
            console.error(`Encryption error for store ${this.storeId}:`, error)
            throw new Error(`Encryption failed: ${error}`)
        }
    }

    /**
     * Déchiffre les données de manière synchrone
     */
    private decrypt(encryptedText: string): string {
        try {
            const bytes = CryptoJS.AES.decrypt(encryptedText, this.encryptionKey)
            const decrypted = bytes.toString(CryptoJS.enc.Utf8)

            if (!decrypted) {
                throw new Error('Decryption resulted in empty string')
            }

            return decrypted
        } catch (error) {
            console.error(`Decryption error for store ${this.storeId}:`, error)
            throw new Error(`Decryption failed: ${error}`)
        }
    }

    /**
     * Sérialise avec chiffrement synchrone
     */
    serialize(state: any): string {
        if (!state || typeof state !== 'object') {
            const jsonData = JSON.stringify(state)
            return this.createEncryptedFormat(jsonData)
        }

        const jsonData = JSON.stringify(state)
        const cacheKey = this.generateCacheKey(jsonData)

        // Vérifier le cache d'abord
        if (this.cache.has(cacheKey)) {
            return this.cache.get(cacheKey)!
        }

        // Chiffrer de manière synchrone
        const result = this.createEncryptedFormat(jsonData)

        // Mettre en cache
        this.updateCache(cacheKey, result)

        return result
    }

    /**
     * Crée le format chiffré avec métadonnées
     */
    private createEncryptedFormat(jsonData: string): string {
        try {
            const encrypted = this.encrypt(jsonData)
            return JSON.stringify({
                _encrypted: true,
                _version: '1.0',
                data: encrypted,
                timestamp: Date.now(),
                store: this.storeId
            })
        } catch (error) {
            console.error(`Failed to create encrypted format for store ${this.storeId}:`, error)
            // Fallback en cas d'erreur de chiffrement
            return JSON.stringify({
                _encrypted: false,
                _error: true,
                data: jsonData,
                timestamp: Date.now(),
                store: this.storeId
            })
        }
    }

    /**
     * Désérialise avec déchiffrement synchrone
     */
    deserialize(data: string): any {
        if (!data) {
            return {}
        }

        try {
            const parsed = JSON.parse(data)

            // Vérifier si c'est notre format
            if (parsed && typeof parsed === 'object' && '_encrypted' in parsed) {

                // Données avec erreur de chiffrement (fallback)
                if (parsed._error && !parsed._encrypted) {
                    console.warn(`Store ${this.storeId}: using fallback unencrypted data`)
                    return JSON.parse(parsed.data)
                }

                // Données chiffrées
                if (parsed._encrypted && parsed.data) {
                    try {
                        const decrypted = this.decrypt(parsed.data)
                        return JSON.parse(decrypted)
                    } catch (error) {
                        console.error(`Failed to decrypt data for store ${this.storeId}:`, error)
                        return {}
                    }
                }
            }

            // Format legacy ou JSON direct
            return parsed
        } catch (error) {
            console.error(`Deserialization error for store ${this.storeId}:`, error)
            return {}
        }
    }

    /**
     * Génère une clé de cache simple
     */
    private generateCacheKey(content: string): string {
        let hash = 0
        for (let i = 0; i < Math.min(content.length, 50); i++) {
            const char = content.charCodeAt(i)
            hash = ((hash << 5) - hash) + char
            hash = hash & hash
        }
        return Math.abs(hash).toString(16)
    }

    /**
     * Met à jour le cache avec limitation de taille
     */
    private updateCache(key: string, value: string) {
        // Nettoyer le cache si trop grand
        if (this.cache.size >= this.maxCacheSize) {
            const firstKey = this.cache.keys().next().value
            this.cache.delete(firstKey)
        }

        this.cache.set(key, value)
    }

    /**
     * Nettoyage des ressources
     */
    cleanup() {
        this.cache.clear()
    }
}

/**
 * Plugin principal avec persistance manuelle et chiffrement synchrone
 */
export default defineNuxtPlugin({
    name: 'pinia-persist',
    parallel: true,

    async setup(nuxtApp) {
        const pinia = nuxtApp.$pinia as Pinia
        const config = useRuntimeConfig()

        // Synchroniser Capacitor vers localStorage
        try {
            await syncCapacitorToStorage()
        } catch (error) {
            console.error('Storage sync error:', error)
        }

        // Configuration environnement
        const isProduction = process.env.NODE_ENV === 'production'
        const encryptionKey = config.public?.encryptionKey ||
            generateDefaultKey(config.public?.appId)

        // Map des serializers par store
        const serializers = new Map<string, SimpleJSONSerializer | SecureCryptoJSSerializer>()

        // Fonction pour créer un serializer selon le mode
        const createSerializer = (storeId: string, isSecure: boolean = false) => {
            const key = `${storeId}-${isSecure ? 'secure' : 'simple'}`
            if (!serializers.has(key)) {
                const serializer = isSecure
                    ? new SecureCryptoJSSerializer(encryptionKey, storeId)
                    : new SimpleJSONSerializer(storeId)
                serializers.set(key, serializer)
            }
            return serializers.get(key)!
        }

        // DÉSACTIVER la persistance automatique globale
        // Hook pour intercepter la création des stores
        pinia.use(({ store, options }) => {
            const storeId = store.$id

            // Vérifier si le store demande une sécurisation
            let shouldSecure = false

            // Méthode 1: Via persist.secure
            if (typeof options.persist === 'object' && options.persist !== null && !Array.isArray(options.persist)) {
                shouldSecure = (options.persist as any).secure === true
            }

            // Méthode 2: Via une option personnalisée secure (fallback)
            if (!shouldSecure && (options as any).secure === true) {
                shouldSecure = true
            }

            // Méthode 3: Via le nom du store (convention)
            if ((!shouldSecure && isProduction) && (storeId.includes('secure') || storeId.includes('auth') || storeId.includes('private'))) {
                console.log(`Store '${storeId}': auto-detected as secure based on naming convention`)
                shouldSecure = true
            }

            const key = `${isProduction ? 'p' : 'd'}eco-${(options.persist as any).key || storeId}`
            // TOUJOURS désactiver la persistance automatique
            options.persist = false

            // Créer le serializer approprié
            const serializer = shouldSecure
                ? createSerializer(storeId, true) as SecureCryptoJSSerializer
                : createSerializer(storeId, false) as SimpleJSONSerializer


            console.log(`Store '${storeId}': ${shouldSecure ? 'secure' : 'standard'} persistence with crypto-js`)

            // Variables pour éviter les boucles
            let isInitialized = false
            let lastSerializedState = ''
            let isSerializing = false

            // Restaurer l'état au démarrage (une seule fois)
            if (!isInitialized) {
                try {
                    const data = capacitorStorageSync.getItem(key)
                    if (data) {
                        const parsed = JSON.parse(data)

                        // Vérifier que ce sont bien des données en clair
                        if (!parsed._encrypted && shouldSecure) {
                            const serializedState = serializer.serialize(parsed)
                            capacitorStorageSync.setItem(key, serializedState)
                            lastSerializedState = JSON.stringify(parsed)
                            store.$patch(parsed)
                        }
                        else if (shouldSecure || (parsed._encrypted && !shouldSecure)){
                            const restoredState = serializer.deserialize(data)
                            if (restoredState && Object.keys(restoredState).length > 0) {
                                lastSerializedState = JSON.stringify(restoredState)
                                store.$patch(restoredState)
                                console.log(`Store '${storeId}': state restored successfully`)
                            }
                        }
                        else{
                            lastSerializedState = JSON.stringify(parsed)
                            store.$patch(parsed)
                        }

                    }
                    isInitialized = true
                } catch (error) {
                    console.error(`Failed to restore store ${storeId}:`, error)
                    isInitialized = true
                }
            }

            // Persistance synchrone manuelle avec protection contre les boucles
            store.$subscribe((mutation, state) => {
                // Éviter la sérialisation pendant une sérialisation
                if (isSerializing) {
                    return
                }

                try {
                    const currentState = JSON.stringify(state)

                    // Éviter la sérialisation répétée du même état
                    if (currentState === lastSerializedState) {
                        return
                    }

                    isSerializing = true
                    lastSerializedState = currentState

                    // Sérialisation synchrone - pas de problème de récursion
                    const serializedState = serializer.serialize(state)
                    capacitorStorageSync.setItem(key, serializedState)

                } catch (error) {
                    console.error(`Failed to persist store ${storeId}:`, error)
                } finally {
                    isSerializing = false
                }
            }, { detached: true })
        })

        // Nettoyage
        if (isProduction) {
            setTimeout(() => cleanupOldData(), 10000)
        }

        if (typeof window !== 'undefined') {
            document.addEventListener('visibilitychange', () => {
                if (document.visibilityState === 'hidden') {
                    serializers.forEach(serializer => serializer.cleanup())
                }
            })
        }

        // Utilitaires exposés
        nuxtApp.provide('persistedStorage', {
            async clearAll() {
                try {
                    // localStorage
                    for (let i = localStorage.length - 1; i >= 0; i--) {
                        const key = localStorage.key(i)
                        if (key?.includes('eco-')) {
                            localStorage.removeItem(key)
                        }
                    }

                    // Capacitor
                    if ((await import('@capacitor/core')).Capacitor.isNativePlatform()) {
                        const { Preferences } = await import('@capacitor/preferences')
                        const { keys } = await Preferences.keys()
                        for (const key of keys) {
                            if (key.includes('eco-')) {
                                await Preferences.remove({ key })
                            }
                        }
                    }

                    // Cleanup serializers
                    serializers.forEach(serializer => serializer.cleanup())
                    serializers.clear()

                    console.log('All persisted data cleared')
                } catch (error) {
                    console.error('Error clearing data:', error)
                }
            },

            getStorageSize(): number {
                let size = 0
                for (let key in localStorage) {
                    if (key.includes('eco-')) {
                        const value = localStorage.getItem(key)
                        if (value) size += key.length + value.length
                    }
                }
                return size
            },

            getStoreInfo(storeId: string) {
                const key = `${isProduction ? 'p' : 'd'}eco-${storeId}`
                const data = capacitorStorageSync.getItem(key)
                let isEncrypted = false
                let isJSON = false
                let hasError = false

                try {
                    if (data) {
                        const parsed = JSON.parse(data)
                        isEncrypted = parsed._encrypted === true
                        hasError = parsed._error
                        isJSON = !isEncrypted && !hasError
                    }
                } catch {
                    // Ignore parsing errors
                }

                return {
                    id: storeId,
                    isEncrypted,
                    isJSON,
                    hasError,
                    keyPrefix: key,
                    hasData: !!data,
                    serializationType: isEncrypted ? 'crypto-js-encrypted' : 'json',
                    library: isEncrypted ? 'crypto-js' : 'native-json'
                }
            },

            getAllStoresInfo() {
                const stores: any[] = []

                for (let i = 0; i < localStorage.length; i++) {
                    const key = localStorage.key(i)
                    if (key?.includes('eco-')) {
                        const storeId = key.replace(/^[dp]eco-/, '')
                        stores.push(this.getStoreInfo(storeId))
                    }
                }

                return stores
            },

            // Teste le chiffrement/déchiffrement pour un store donné
            testEncryption(storeId: string, testData: any = { test: 'data' }) {
                try {
                    const serializer = createSerializer(storeId, true) as SecureCryptoJSSerializer
                    const encrypted = serializer.serialize(testData)
                    const decrypted = serializer.deserialize(encrypted)

                    return {
                        success: JSON.stringify(testData) === JSON.stringify(decrypted),
                        original: testData,
                        encrypted: encrypted.length,
                        decrypted: decrypted
                    }
                } catch (error) {
                    return {
                        success: false,
                        error: error.message
                    }
                }
            }
        })
    }
})

/**
 * Génère une clé déterministe
 */
function generateDefaultKey(appName: string = 'eco-construction'): string {
    const baseKey = `${appName}-2024-secure-key-cryptojs`
    let hash = 0

    for (let i = 0; i < baseKey.length; i++) {
        const char = baseKey.charCodeAt(i)
        hash = ((hash << 5) - hash) + char
        hash = hash & hash
    }

    return Math.abs(hash).toString(16).padStart(32, '0')
}

/**
 * Nettoie les anciennes données
 */
function cleanupOldData() {
    try {
        const now = Date.now()
        const maxAge = 30 * 24 * 60 * 60 * 1000 // 30 jours

        for (let i = localStorage.length - 1; i >= 0; i--) {
            const key = localStorage.key(i)
            if (!key?.includes('eco-')) continue

            try {
                const item = localStorage.getItem(key)
                if (!item) continue

                const data = JSON.parse(item)
                if (data.timestamp && (now - data.timestamp) > maxAge) {
                    localStorage.removeItem(key)
                    console.log(`Cleaned old data: ${key}`)
                }
            } catch {
                // Ignorer les erreurs de parsing
            }
        }
    } catch (error) {
        console.error('Cleanup error:', error)
    }
}